import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import AuditExamParticipations from './AuditExamParticipations'
import noAssignmentImg from '../../../assets/noassignment.svg'
import { Analytics, OpenInNew } from '@mui/icons-material'
import { useUserStore } from '../../../store/user.store'
import { formatDate } from '../../../utils/utilities'
import AuditExamViewer from './AuditExamViewer'
import api from '../../../service/api'

const FacultyAuditExamsList = () => {

    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByAssignmentCount: "1" } )
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [exams, setExams] = useState( [] )
    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [selectedExam, setSelectedExam] = useState( null )
    const [partipationsViewerState, setPartipationsViewerState] = useState( false )

    const navigate = useNavigate()

    const { layout } = useTheme()

    const UserStore = useUserStore()

    const columns = useMemo( () => [
        { id: 2, name: "Exam name", field: "exam_name", padding: "10px 20px" },
        { id: 3, name: 'Faculty name', field: "faculty_name", renderCell: row => row.faculty_name?.toLowerCase(), linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.created_by}?legacy=${row.legacy_system_data}`, cellStyles: { textTransform: "capitalize" }, minWidth: "150px", align: "left", padding: "10px 20px" },
        { id: 6, name: 'Department', field: "department", renderCell: row => row?.created_by_data?.branch_name?.toLowerCase(), cellStyles: { textTransform: "capitalize" }, padding: "10px 20px" },
        { id: 4, field: "subject", renderCell: row => row.subject?.subject_name || "Not available", name: "Subject", padding: "10px 20px" },
        { id: 5, align: "center", field: "created_at", renderCell: row => formatDate( row.created_at ), name: "Created on", width: "150px", padding: "10px 20px" },
        { id: 7, align: "center", field: "attempts", name: "Attempts", padding: "10px 20px" },
        {
            id: 7, align: "right", field: "", renderCell: row => ( <Box display="flex" justifyContent="flex-end" gap="10px" alignItems="center">
                <Tooltip title="Open details">
                    <IconButton onClick={() => setSelectedExam( row )} color="primary" size="small"><OpenInNew fontSize='small' /></IconButton>
                </Tooltip>
                {+row.attempts > 0 && <Tooltip title="View participations">
                    <IconButton onClick={() => { setSelectedExam( row ); setPartipationsViewerState( true ) }} color="secondary" size="small"><Analytics fontSize='small' /></IconButton>
                </Tooltip>}
            </Box> ), name: "Actions", padding: "10px 20px"
        },
    ], [] )


    const filterFunction = item => {
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            return ( item.legacy_system_data && +legacy_department_id === +item?.created_by_data?.branch_id ) || ( !item.legacy_system_data && +department_id === +item?.created_by_data?.branch_id )
        } else {
            return true
        }
    }

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'selectedInstitute', "all" )
            prev.set( 'selectedDepartment', "all" )
            return prev
        } )
    }

    const handleDialogClose = () => {
        setSelectedExam( null )
        setPartipationsViewerState( false )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getExamsForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setExams( data )
            setError( null )
        } catch ( err ) {
            console.log( err )
            setError( { code: err?.response?.status, message: err?.message?.data?.message || "Something went wrong while fetching exams." } )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute] )


    useEffect( () => {
        getData()
    }, [getData] )


    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {selectedExam && <AuditExamViewer setExamList={setExams} exam={selectedExam} handleClose={handleDialogClose} openState={!partipationsViewerState && Boolean( selectedExam )} />}
            {selectedExam && partipationsViewerState && <AuditExamParticipations exam={selectedExam} handleClose={() => { setPartipationsViewerState( false ); setSelectedExam( null ) }} openState={partipationsViewerState} />}

            <Box display="flex" gap={layout.cardsGap} alignItems="flex-end" justifyContent="space-between" >
                <Typography variant="h1" fontWeight="600" component="h2" fontSize="14px">Exams</Typography>
                <Button size="small" onClick={() => navigate( `/${["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? "admin" : "audit"}/exams` )} variant="contained" color='secondary' disableElevation sx={{ height: "33.5px", textTransform: "capitalize", }} >View Stats</Button>
            </Box >

            {!loading && !error && exams.filter( filterFunction ).length > 0 && <CustomTable tableStyles={{ maxHeight: "450px" }} globalSearch="Search by faculty or subject or exam title" columns={columns} data={exams.filter( filterFunction )} />}

            {!loading && !error && exams.filter( filterFunction ).length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <img src={noAssignmentImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No exams were found for applied filters!</Typography>
                <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>
            </Box>}
            {loading && !error && <DataLoadingSpinner padding="0" waitingMessage="Loading exams please wait...." />}
            {error && <TryAgainMessageBlock err={error?.message} code={error.code} loading={loading} getData={getData} />}
        </Box>
    )
}

export default FacultyAuditExamsList
